import log from 'shared/util/log'
import { FALLBACK_CLIP_TITLE } from '../../../../constants'
import { CreationToolSourceFile } from '../../../../types'
import getUnvalidatedSource from '../../../../utils/get-unvalidated-source'
import { ICapture, ISegment } from '../types'
import Imaging from './imaging'
import { getMergedWatermark } from './utils'

const SESSION_STORAGE_KEY = 'SUPERCUT_UPLOAD_DATA'

const processImagingUrl = async (url: string, filename: string = FALLBACK_CLIP_TITLE): Promise<File> => {
    const blob = await fetch(url).then(response => response.blob())
    return new File([blob], filename, {
        type: blob.type,
        lastModified: Date.now(),
    })
}

class Uploader {
    static setUploadSessionData = data => {
        window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data))
    }

    static getUploadSessionData = () => {
        return JSON.parse(window.sessionStorage.getItem(SESSION_STORAGE_KEY) || '{}')
    }

    static clearUploadSessionData = () => {
        Uploader.setUploadSessionData({})
    }

    static getVideoURLs = async (segments: ISegment[], capture?: ICapture) => {
        return Promise.all(
            segments.map(async segment => {
                let url = segment.video_url

                if (!segment.editParams && !capture?.watermark) {
                    return url
                }

                const { trim, crop, watermark } = segment.editParams || {}

                // trim
                if (trim) {
                    url = Imaging.trim(url, trim.start, trim.end - trim.start)
                }

                // crop
                if (crop) {
                    url = Imaging.crop(url, crop.x, crop.y, crop.width, crop.height)
                }

                // capture watermark
                if (capture?.watermark) {
                    try {
                        // merged capture and segment watermark
                        const watermarkUrl = await getMergedWatermark(segment, capture)
                        url = await Imaging.burnWatermark(url, watermarkUrl)
                    } catch (e) {
                        log.error(e)
                    }
                } else if (watermark) {
                    // segment watermark only
                    url = await Imaging.burnWatermark(url, watermark)
                }

                return url
            })
        )
    }

    static processURLs = async (urls: string[], capture: ICapture) => {
        const processedSources: CreationToolSourceFile[] = []

        for (let i = 0; i < urls.length; i++) {
            let source: string | File = urls[i]

            if (source.indexOf(`imaging.giphy.com`) > -1) {
                source = await processImagingUrl(source, source)
            }

            let processedFile: CreationToolSourceFile = getUnvalidatedSource(source)

            if (capture.title) {
                processedFile.title = capture.title
            }

            if (capture.upload_metadata?.tags) {
                processedFile.tags = capture.upload_metadata?.tags.split(',')
            }

            if (capture.upload_metadata?.source_url) {
                processedFile.sourceUrl = capture.upload_metadata?.source_url
            }

            if (capture.username) {
                processedFile.username = capture.username
            }

            if (capture.upload_metadata?.rating) {
                processedFile.rating = capture.upload_metadata?.rating
            }

            if (capture.upload_metadata?.is_private) {
                processedFile.isPrivate = capture.upload_metadata?.is_private
            }

            processedSources.push(processedFile)
        }
        return processedSources
    }
}

export default Uploader
